import { FC, useCallback, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { Campaign } from '@mui/icons-material';
import { api } from 'services/api';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import { TPgEmailType, TPgNotification } from 'services/api/paymentGateway/types';
import { DATE_FILTER } from 'ui/EasTable/constants';
import { EIconButtons } from 'ui/EasTable/EasTableRow/EasTableValue/constants';
import { ESortDirection, ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { notificationContainer } from 'utils/notificationContainer';
import { PgConfirmDialog } from '../common/PgConfirmDialog';
import { PgList } from '../common/PgList';
import { PgMiniTable, PgStatusChip } from '../common/PgMiniTable';
import { PgPanel } from '../common/PgPanel';
import { useReloadKey } from '../common/helpers';
import { pgActionsColumn, pgColumn, statusColumn } from '../common/tableHelpers';
import { RelinkCampaignDialog } from '../Customers/RelinkCampaignDialog';

type TRow = TPgNotification;
const col = (
	name: string,
	selector: keyof TRow & string,
	type: ETableStructureType,
	extra: Partial<TTableStructure<keyof TRow, TRow>> = {},
) => pgColumn<TRow>(name, selector, type, extra);

const RESEND = 'Resend';

const STRUCTURE: TTableStructure<keyof TRow, TRow>[] = [
	col('Created', 'createdAt', ETableStructureType.DATE_TIME, {
		sortable: true,
		filterable: true,
		filter: DATE_FILTER,
	}),
	col('Type', 'notificationType', ETableStructureType.STRING, { sortable: true, filterable: true }),
	col('Status', 'status', ETableStructureType.STATUS, {
		sortable: true,
		filterable: true,
		...statusColumn(['queued', 'sent', 'failed', 'skipped']),
	}),
	col('Recipients', 'recipients', ETableStructureType.STRING, {
		value: (item) => item.recipients.join(', '),
	}),
	col('Subject', 'subject', ETableStructureType.STRING, { tooltip: (item) => item.subject ?? '' }),
	col('Customer ID', 'customerId', ETableStructureType.STRING, {
		sortable: true,
		filterable: true,
		value: (item) => (item.customerId === null ? '' : String(item.customerId)),
	}),
	col('Error', 'error', ETableStructureType.STRING, { tooltip: (item) => item.error ?? '' }),
	col('Sent', 'sentAt', ETableStructureType.DATE_TIME),
	pgActionsColumn<TRow>([{ name: RESEND, label: 'Resend', icon: EIconButtons.SEND }]),
];

/**
 * E-mails of the gateway (03 §4.11): the list with "Resend", the state of the e-mail types (templates
 * in the e-mail service) and the re-link campaign.
 */
export const NotificationsView: FC = () => {
	const [types, setTypes] = useState<TPgEmailType[] | null>(null);
	const [typesError, setTypesError] = useState<string | null>(null);
	const [resendRow, setResendRow] = useState<TRow | null>(null);
	const [resendBusy, setResendBusy] = useState(false);
	const [campaignOpen, setCampaignOpen] = useState(false);
	const [reloadKey, reload] = useReloadKey();

	useEffect(() => {
		api.paymentGateway
			.getEmailTypes()
			.then(setTypes)
			.catch((e) => setTypesError(getPgErrorMessage(e, 'Failed to load the e-mail types')));
	}, []);

	const handleAction = useCallback((row: TRow, action: string) => {
		if (action === RESEND) setResendRow(row);
	}, []);

	const resend = async () => {
		if (!resendRow) return;
		setResendBusy(true);
		try {
			const sent = await api.paymentGateway.resendNotification(resendRow.id);
			notificationContainer(
				sent ? 'The e-mail is sent again' : 'Nothing was sent',
				sent ? 'success' : 'warning',
			);
			setResendRow(null);
			reload();
		} catch (e) {
			notificationContainer(getPgErrorMessage(e, 'Resend failed'), 'error');
		} finally {
			setResendBusy(false);
		}
	};

	const missing = (types ?? []).filter((type) => !type.exists || !type.active);

	return (
		<>
			<PgPanel
				title={`E-mail types${missing.length ? ` (${missing.length} not ready)` : ''}`}
				actions={
					<Button size="small" startIcon={<Campaign />} onClick={() => setCampaignOpen(true)}>
						Re-link campaign
					</Button>
				}
			>
				{typesError ? (
					<span>{typesError}</span>
				) : (
					<PgMiniTable
						rows={types ?? []}
						getKey={(row) => row.key}
						emptyText="Loading…"
						columns={[
							{ header: 'Type', render: (row) => row.key },
							{ header: 'Template handle', render: (row) => row.handle },
							{
								header: 'Template',
								render: (row) => (
									<PgStatusChip
										value={row.exists ? 'active' : 'failed'}
										label={row.exists ? 'Exists' : 'Missing'}
									/>
								),
							},
							{
								header: 'Active',
								render: (row) => (
									<PgStatusChip
										value={row.active ? 'active' : 'disabled'}
										label={row.active ? 'Active' : 'Inactive'}
									/>
								),
							},
						]}
					/>
				)}
			</PgPanel>
			<PgList<TRow>
				title="E-mails"
				structure={STRUCTURE}
				fetcher={api.paymentGateway.getNotifications}
				onAction={handleAction}
				defaultOrder={{ fieldName: 'createdAt', direction: ESortDirection.DESC }}
				reloadKey={reloadKey}
				errorText="Failed to load e-mails"
			/>
			<PgConfirmDialog
				open={!!resendRow}
				title="Resend the e-mail"
				confirmLabel="Resend"
				confirmColor="warning"
				busy={resendBusy}
				onConfirm={() => void resend()}
				onClose={() => setResendRow(null)}
				description={`The e-mail "${resendRow?.notificationType ?? ''}" is sent again to ${
					resendRow?.recipients.join(', ') || 'its recipients'
				}.`}
			/>
			<RelinkCampaignDialog open={campaignOpen} onClose={() => setCampaignOpen(false)} />
		</>
	);
};
